import * as Yup from 'yup';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';
import { Trans, useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import closeFill from '@iconify/icons-eva/close-fill';
// hooks
import useAuth from 'src/hooks/useAuth';
import useIsMountedRef from 'src/hooks/useIsMountedRef';
// material
import { Stack, Alert, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { makeStyles } from '@mui/styles';
import AlertTitle from '@mui/material/AlertTitle';
import { INITIAL_PATH } from 'src/constants';
import { QRCode } from '../QRCode';
import { MfaSetNavigation } from '../../SelectMfaMethod';
import { MIconButton } from 'src/components/@material-extend';
import { useSnackbar } from 'notistack5';

type InitialValues = {
  authCode: string;
  afterSubmit?: string;
};

type AddTotpDeviceFormProps = {
  navigationPaths: MfaSetNavigation;
};

const useStyles = makeStyles({
  root: {
    background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
    border: 0,
    borderRadius: 8,
    boxShadow: '0 3px 5px 2px rgba(255, 105, 135, .3)',
    color: 'white',
    height: 48,
    padding: '0 30px',
  },
});

/**
 * TOTPデバイス登録Form
 * @returns AddTotpDeviceForm
 */
export const AddTotpDeviceForm = ({ navigationPaths }: AddTotpDeviceFormProps) => {
  const { setUpSoftwareToken, verifySoftwareToken, isAuthenticated, isRequiredMfa, isMfaAuthenticated } = useAuth();
  const isMountedRef = useIsMountedRef();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const AddTotpDeviceSchema = Yup.object().shape({
    authCode: Yup.string().required(t('validation.required-input', { item: t('mfa.authCode') })),
  });
  const [mfaSetUpUrl, setMfaSetUpUrl] = useState<string | null>(null);
  //  認証完了済みフラグ(MFA不要でID/Pass認証済み、またはMFA必須でMFA認証済みの場合True)
  const isAuthed = (!isRequiredMfa && isAuthenticated) || (isRequiredMfa && isMfaAuthenticated);

  useEffect(() => {
    // MFAデバイス登録用のURL取得
    const setUpMfaSoftwareToken = async () => {
      const url = await setUpSoftwareToken();
      setMfaSetUpUrl(url?.href ?? '');
    };
    setUpMfaSoftwareToken();

    // 認証コードtextboxにフォーカス
    if (inputRef?.current) inputRef.current.focus();
  }, []);

  const formik = useFormik<InitialValues>({
    initialValues: {
      authCode: '',
    },
    validationSchema: AddTotpDeviceSchema,
    onSubmit: async (values, { setErrors, setSubmitting, resetForm }) => {
      try {
        // デバイス登録・TOTP認証実行
        await verifySoftwareToken(values.authCode);

        // MFA認証成功メッセージ表示
        enqueueSnackbar(t('notification.login.success'), {
          variant: 'success',
          action: (key) => (
            <MIconButton size="small" onClick={() => closeSnackbar(key)}>
              <Icon icon={closeFill} />
            </MIconButton>
          ),
        });
        if (isMountedRef.current) {
          setSubmitting(false);
          // 呼び出し元画面の設定があれば (=ログイン後の画面から遷移した場合)、その画面へ戻る
          // なければ（=ユーザーの初回MFA設定時)、HOME画面へ
          navigate(navigationPaths.caller ?? INITIAL_PATH, { replace: true });
          return;
        }
      } catch (err: any) {
        console.error(err);
        resetForm();
        setSubmitting(false);
        setErrors({ afterSubmit: getErrorMessage(err) });
      }
    },
  });

  // メッセージ管理
  const getErrorMessage = useCallback(
    (err: any): string => {
      if (err instanceof Error) {
        const msg = () => {
          switch (err.name) {
            case 'EnableSoftwareTokenMFAException': // 認証コード不一致
            case 'InvalidParameterException': // 認証コード入力値エラー
              return t('validation.mismatch', { item: t('mfa.authCode') });
            default:
              return err.message;
          }
        };
        return msg();
      }
      return err.toString();
    },
    [t],
  );

  const { errors, touched, isSubmitting, handleSubmit, getFieldProps } = formik;

  const classes = useStyles();

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" onSubmit={handleSubmit}>
        <Stack spacing={2}>
          <>
            {!mfaSetUpUrl && <Typography sx={{ color: 'text.secondary', mb: 5 }}></Typography>}
            {mfaSetUpUrl && (
              <Stack spacing={2}>
                <label>
                  <Trans i18nKey={'mfa.addTotpDevice.instructions'} />
                </label>
                {/* ログイン後のMFA変更時に表示する注意メッセージ */}
                <Alert severity="warning">
                  <AlertTitle>{t('mfa.caution')}</AlertTitle>
                  <Trans i18nKey={isAuthed ? 'mfa.addTotpDevice.cautionDetailAuthed' : 'mfa.addTotpDevice.cautionDetail'} />
                </Alert>
                <Alert severity="warning">
                  <AlertTitle>{t('mfa.recommendation')}</AlertTitle>
                  <Trans i18nKey={'mfa.addTotpDevice.recommendationDetail'} />
                </Alert>
                {/* TOTPデバイス登録用QRコード表示 */}
                <QRCode url={mfaSetUpUrl} />
                {/* 認証コード入力 */}
                <TextField
                  fullWidth
                  size="medium"
                  autoComplete="one-time-code"
                  type="text"
                  label={t('mfa.authCode')}
                  {...getFieldProps('authCode')}
                  error={Boolean(touched.authCode && errors.authCode)}
                  helperText={touched.authCode && errors.authCode}
                  inputRef={inputRef}
                />
              </Stack>
            )}
            {errors.afterSubmit && <Alert severity="error">{errors.afterSubmit}</Alert>}
          </>
          <LoadingButton fullWidth size="large" type="submit" variant="contained" loading={isSubmitting} className={classes.root}>
            {t('button.send')}
          </LoadingButton>
        </Stack>
      </Form>
    </FormikProvider>
  );
};
